<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onUpdated, ref, watch } from 'vue'
import { PAGES } from './constants'
import { useNavigation } from './useNavigation'
import { useStudentScreen } from './useStudentScreen'
import StudentModeBadge from './StudentModeBadge.vue'
import TeacherInfoMenu from './TeacherInfoMenu.vue'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

const props = defineProps<{ context: Student2Context; unitId: number }>()
const emit = defineEmits(['change-mode', 'stars-info'])

const { navigationState, getLink: $getLink, gotoOnClick: $gotoOnClick } = useNavigation()
const { isMobileLocal } = useStudentScreen()

const container = ref<HTMLElement | null>(null)
const unitListAbsolute = ref(true)

const currentUnit = computed(() => {
  return props.context.course_part.units.find(e => e.id === props.unitId)
})

let resizeObserver: ResizeObserver | null = null

function onElementChange() {
  if (!container.value) return
  if (!container.value.firstElementChild || !container.value.lastElementChild) return

  let containerWidth = container.value.clientWidth
  const containerStyles = getComputedStyle(container.value)
  containerWidth -= parseFloat(containerStyles.paddingLeft)
  containerWidth -= parseFloat(containerStyles.paddingRight)

  const leftElementWidth = container.value.firstElementChild.getBoundingClientRect().width
  const rightElementWidth = container.value.lastElementChild.getBoundingClientRect().width

  const safeMargins = Math.max(leftElementWidth, rightElementWidth) * 2
  unitListAbsolute.value = containerWidth - safeMargins > 920
}

watch(
  isMobileLocal,
  async () => {
    await nextTick()
    resizeObserver?.disconnect()

    resizeObserver = new ResizeObserver(onElementChange)
    if (container.value) resizeObserver.observe(container.value)

    onElementChange()
  },
  { immediate: true }
)

onUpdated(() => {
  onElementChange()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

const isFloating = computed(() => {
  const tab = navigationState.params.tab
  return PAGES.find(entry => entry.name === tab)?.floatingNav ?? false
})

const studentName = computed(() => {
  const student = props.context.student
  return [student.name, student.last_name].join(' ')
})
</script>

<template>
  <header
    ref="container"
    class="student-viewunit-header"
    :class="{
      'student-viewunit-header_floating': isFloating,
      'student-viewunit-header_unit-list-absolute': unitListAbsolute
    }"
  >
    <v-menu-base>
      <template #activator="{ onclick }">
        <h1 @click="onclick" class="student-viewunit-header__title">
          {{ currentUnit?.title }}
          <v-icon size="32">mdi-menu-down</v-icon>
        </h1>
      </template>

      <div class="pa-1" style="min-width: 200px">
        <v-menu-button
          v-for="unit in context.course_part.units"
          :key="unit.id"
          :href="$getLink({ unitId: unit.id })"
          @click="$gotoOnClick"
        >
          <p>
            {{ unit.title }}
            <span class="text-caption gray-text" v-if="context.smartbook.last_unit_unlocked_by_progress === unit.id">
              <br />Текущий юнит
            </span>
          </p>

          <template #append>
            <v-icon class="green-text" v-if="unit.id === props.unitId">mdi-check</v-icon>
          </template>
        </v-menu-button>
      </div>
    </v-menu-base>

    <div class="student-viewunit-header__mobile-header">
      <span class="text-overline text-right">{{ studentName }}</span>

      <v-menu-base v-if="context.student.id !== 0 && !context.impersonated" pos-horizontal="right" no-mobile-modal>
        <template #activator="{ onclick }">
          <v-link @click="onclick"><v-icon>mdi-dots-vertical</v-icon></v-link>
        </template>

        <div class="pa-1">
          <v-menu-button mode="primary" :href="$getLink({ hash: 'parent-cabinet' })" @click="$gotoOnClick">
            <v-icon class="mr-2" size="20">mdi-human-male-child</v-icon>
            Перейти в Кабинет родителя
          </v-menu-button>

          <v-menu-button mode="error" :href="$getLink({ hash: 'logout' })" @click="$gotoOnClick">
            <v-icon class="mr-2" size="20">mdi-exit-run</v-icon>
            Выйти из аккаунта
          </v-menu-button>
        </div>
      </v-menu-base>

      <StudentModeBadge :student="context.student" @change-mode="emit('change-mode')" />

      <v-button v-if="context.student.id === 0" mode="primary small" href="/users/login?student_login=true">
        Войти
      </v-button>

      <v-button
        v-else-if="context.student.tails.length"
        mode="primary outlined small"
        :href="$getLink({ tab: 'tails' })"
        @click="$gotoOnClick"
      >
        {{ context.student.tails.length }}
        <img height="24" :src="asset('img/animals/animal-tail-11.png')" class="ml-1" />
      </v-button>

      <TeacherInfoMenu :context="context" />

      <!-- FIXME: temporarily hide stars on I Can Sing -->
      <button
        v-if="context.smartbook && context.course_part.codename === 'icanspeak'"
        v-tooltip:left="'Нажмите, чтобы узнать\nбольше о звёздочках'"
        class="student-viewunit-header__star-count"
        @click="emit('stars-info')"
      >
        <v-icon :size="isMobileLocal ? 32 : 56">3d-star</v-icon>
        {{ (context.smartbook || {}).star_count || 0 }}
      </button>
    </div>
  </header>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-navigation_hidden:not(.student-navigation_floating) ~ .student-viewunit-header {
  left: 0;
}

.student-unit-select_hidden:not(.student-unit-select_floating) ~ .student-viewunit-header {
  right: 0;
}

:root {
  // 24px + 24px = margins of the nav hide button
  // 40px = size of the nav hide button
  --header_height: #{24px * 2 + 40px};
}

@include s2.mobile {
  :root {
    // 8px + 8px = margins of the nav hide button
    // 36px = size of the nav hide button
    --header_height: #{8px * 2 + 36px};
  }
}

.student-viewunit-header {
  height: var(--header_height);
  padding-left: var(--header_height);
  padding-right: 16px;

  display: grid;
  align-items: center;
  grid-template-columns: 1fr minmax(300px, auto) 1fr;
  gap: 16px;

  position: fixed;
  top: 0;
  left: var(--navbar_width);
  right: var(--navbar_width);
  z-index: 1;

  background: white;
  transition: left 0.3s ease;

  @include s2.mobile {
    padding-left: calc(var(--header_height) + 8px);
  }

  &_unit-list-absolute {
    grid-auto-flow: column;
    justify-content: space-between;
    grid-template-columns: unset;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;

    margin-left: 16px;
    cursor: pointer;

    font-size: 36px;
    font-weight: 700;
    line-height: 48px;
    white-space: nowrap;

    @include s2.mobile {
      font-size: 32px;
      line-height: 40px;
    }
  }

  &__mobile-header {
    grid-column: 2 / 4;

    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-right: 16px;
  }

  &__star-count {
    justify-self: end;
    flex: 0 0 56px;
    width: 56px;
    height: 56px;

    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 20px;
    line-height: 20px;
    font-weight: 700;

    position: relative;
    transition:
      transform 0.2s ease,
      filter 0.2s ease;

    &:hover {
      transform: scale(1.05);
      filter: brightness(1.05);
      cursor: pointer;
    }

    .v-icon {
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
    }

    @include s2.mobile {
      flex: 0 0 32px;
      width: 32px;
      height: 32px;
      font-size: 16px;
      line-height: 16px;
    }
  }

  &_floating {
    pointer-events: none;
    background: transparent;
    & > * {
      pointer-events: all;
    }
  }

  &_floating > &__title {
    pointer-events: none;
    opacity: 0;
  }

  &_floating > &__star-count {
    pointer-events: none;
    opacity: 0;
  }

  &_floating > &__mobile-header {
    pointer-events: none;
    opacity: 0;
  }
}
</style>
